import type { AudioAsset, SoundType, Species } from '../../types';
import type { ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { getSpecies, speciesList } from '../../core/region';
import { play, type Playback } from '../../core/media/audio';
import { NEW_SHARES, ROUND_LENGTHS } from '../../core/quiz/settings';
import { emptyFocusReason, focusLabel, type Focus } from '../../core/quiz/focus';
import { openCompare } from './compare';
import { type Facet } from '../../core/compare';
import { traitValueLabel } from '../../core/traits';
import { icon } from './icons';
import { t, tParts } from '../../strings';

// Chrome shared by every quiz mode: the progress bar, the prompt label, the
// choice list, and the end-of-session panel. Mode-specific views layer their own
// rules on top under `.quiz.<mode>`.

export function ensureQuizStyle(): void {
  ensureStyle(
    'quiz-css',
    `
  /* Top padding is deliberately less than the sides. The back arrow carries
     6px of its own (.btn-plain), so 18px up there read as 24 and spent a
     quarter of an inch of a phone screen on the gap above a glyph — height
     this screen would rather give to the answer list. */
  /* flex:1 0 auto against the router outlet (a column flex container — see
     base.css): at least as tall as its content, and otherwise grown to fill
     the screen above the nav bar. The growth is what puts the answered bar
     FLUSH at the foot on a question that fits; without it margin-top:auto only
     reaches the end of a 70vh block and the bar floats 70-odd pixels up the
     screen, which is neither pinned nor in the flow. A shrink factor of 0, so a tall
     question overflows and scrolls rather than being squashed into the
     viewport with its bottom off the end of it.
     
     --after-gap is the distance the answered bar keeps from the nav bar. It is
     the frame's own bottom padding AND the sticky offset, one value, so that
     the bar does not step up or down by a few pixels at the moment it sticks. */
  .quiz { --after-gap:14px;
    padding: 6px var(--pad) var(--after-gap); display:flex; flex-direction:column;
    gap:20px; flex:1 0 auto; min-height:70vh; }
  .quiz .qhead { display:flex; flex-direction:column; gap:10px; }
  .quiz .bar { display:flex; align-items:center; gap:10px; }
  .quiz .bar .back { margin-left:-6px; }
  /* The question sits on the top row, above the progress bar. */
  .quiz .bar .prompt { flex:1; min-width:0; }
  .quiz .count { font-size:12px; color:var(--ink-soft); flex:none;
    font-variant-numeric:tabular-nums; }
  /* Badges ride the header row, between the question and the counter, rather
     than on a line of their own.
     
     Which skill this question is, whether calls are in the mix, which voice
     the clip turned out to be: three small facts that were costing a whole
     centred row apiece — a row whose height a phone screen would rather spend
     on the answers. They belong beside the counter because they are the same
     kind of thing it is: not the question, but what KIND of question, said
     once and quietly. */
  .quiz .bar .tags { display:flex; align-items:center; gap:5px; flex:none; }
  /* The badge shape three modes each defined for themselves, moved here when a
     fourth and fifth needed it for the focus tag. Scoped to the slot rather
     than to a mode, so it reaches every view that has one. */
  .tags .hardtag { color:var(--accent); border:1.5px solid currentColor;
    border-radius:999px; padding:3px 8px; font-size:8.4px; letter-spacing:.1em;
    line-height:1; flex:none; }
  .quiz .hint { text-align:center; font-size:12px; color:var(--ink-soft); font-style:italic; }
  /* What the binomial means, under the binomial, once the question is answered.
     The Latin words keep the italic they wear everywhere else and the glosses
     do not, so the line reads as two translations rather than as a sentence.

     Ink INHERITED and softened, never a grey token — same rule as .sci above,
     and for the same reason: this line lands inside the answer row, which by
     then is filled green, and a fixed dark grey on that fill is unreadable. */
  .quiz .etym { margin-top:6px; font-family:var(--font-body); font-size:12.5px;
    line-height:1.45; color:inherit; opacity:.8; font-weight:400;
    font-variation-settings:normal; letter-spacing:0; }
  /* Softening 12.5px ink to .8 costs nothing on linen and costs contrast on a
     filled row: linen at .8 over the moss green measures about 3.7:1, under the
     4.5:1 a line this size needs. The filled states take it back most of the
     way — the size and the upright face are already doing the "this is a note"
     work that the opacity was there for. */
  .quiz .choice.correct .etym, .quiz .choice.wrong .etym { opacity:.95; }
  .quiz .etym .word { font-style:italic; }
  .quiz .etym .sep { opacity:.55; padding:0 5px; }
  .quiz .choices { display:flex; flex-direction:column; gap:11px; margin-top:8px; }
  /* A column, explicitly: .btn is a centred flex ROW, which laid the common name
     and the binomial side by side and then centred the pair — so a short name
     ("Varied Thrush") sat indented while a long one started at the padding edge.
     Stacking them left-aligned is what the design always assumed. */
  .quiz .choice { position:relative; text-align:left; width:100%;
    display:flex; flex-direction:column; align-items:stretch; justify-content:center;
    gap:0;
    font-family:var(--font-display); font-size:18px;
    /* Lighter than the .btn default while the question is still open: four
       display-serif names at 500 read as four headlines shouting at each other,
       and the one that matters is whichever you are about to pick. The result
       states below take the weight back, so answering RESOLVES the list into
       one bold row instead of leaving five. */
    font-weight:420;
    /* Right padding reserves room for the result mark so nothing reflows on answer. */
    padding:15px 56px 15px 18px; }
  /* The binomial takes the row's own ink, softened — never a fixed grey. Once a
     row fills in green or red, a grey subtitle on it is close to unreadable, and
     a per-state override would have to be re-matched on every result class. */
  .quiz .choice .sci { font-size:12.5px; color:inherit; opacity:.72; font-style:italic;
    margin-top:2px; display:block; }
  /* ---- Result states -----------------------------------------------------
     Four of them, so the list itself narrates what happened: the pick you got
     right (solid green), the pick you got wrong (red), the answer you missed
     (pale green), and the rows you never touched (dimmed to grey). Without the
     last two a correct pick and a revealed answer looked identical, which is
     the whole reason it was hard to tell whether you'd been right. */
  .quiz .choice.correct, .quiz .choice.wrong, .quiz .choice.answer { font-weight:560; }
  .quiz .choice.correct { background:var(--moss); color:var(--on-dark);
    border-color:var(--honey-soft); }
  .quiz .choice.wrong { background:var(--danger); color:var(--on-danger); border-color:transparent; }
  .quiz .choice.answer { background:var(--moss-pale); color:var(--on-moss-pale);
    border-color:var(--moss); }
  .quiz .choice:disabled { opacity:1; }
  .quiz .choice.dim { opacity:.45; }
  /* Result mark: hand-drawn pressed-leaf tick / snapped twig, masked over
     currentColor so it inherits the state colour in both themes. Colour is
     therefore never the only signal that an answer was right or wrong. */
  .quiz .choice.correct::after, .quiz .choice.wrong::after, .quiz .choice.answer::after {
    content:''; position:absolute; right:15px; top:50%; transform:translateY(-50%);
    width:28px; height:28px; background:currentColor;
    -webkit-mask-repeat:no-repeat; mask-repeat:no-repeat;
    -webkit-mask-position:center; mask-position:center;
    -webkit-mask-size:contain; mask-size:contain; }
  .quiz .choice.correct::after, .quiz .choice.answer::after {
    -webkit-mask-image:var(--ico-check-bold); mask-image:var(--ico-check-bold); }
  .quiz .choice.wrong::after {
    -webkit-mask-image:var(--ico-cross-bold); mask-image:var(--ico-cross-bold); }
  /* The pale row's ink is body ink, but its tick should still read as green. */
  .quiz .choice.answer::after { background:var(--moss); }
  .quiz .next { margin-top:auto; width:100%; padding:16px; font-size:17px; }
  /* ---- The answered bar --------------------------------------------------
     Undo, Compare and Next on ONE row, PINNED to the foot of the screen from
     the moment a question is answered.
  
     It used to be three stacked rows that ended wherever the answer list
     happened to end, which on a long question meant the way forward was below
     the fold: you answered, then scrolled to find out you were right and
     scrolled again to go on. Sticky, the bar meets the thumb where the thumb
     already is, and the list scrolls underneath it.
  
     Clear of the nav bar, not under it: the bar is sticky at bottom:0 with a
     z-index above the outlet, so a bottom of 0 here would file Next behind
     Home/Library/Stats/You. --navbar-h starts as a token (tokens.css) and is
     overwritten with the bar's measured height by nav-bar.ts. */
  .quiz .after { margin-top:auto; position:sticky; z-index:5;
    bottom:var(--navbar-h);
    display:flex; align-items:center; gap:8px;
    /* Bled to the full width, and DOWN to the nav bar, so the opaque
       background covers every strip a choice row could otherwise be seen
       scrolling through: the two gutters either side of the content box, and —
       the one that actually looked broken — the gap the bar keeps above the
       nav bar, where the row after the last visible one sat in plain sight
       under the buttons.
       
       The gap is the bar's own bottom PADDING rather than a sticky offset, so
       the background owns it. The negative bottom margin cancels the frame's
       matching padding, which puts the bar in exactly the same place when it
       is resting in the flow as when it is stuck — no step at the moment it
       sticks. */
    margin-left:calc(var(--pad) * -1); margin-right:calc(var(--pad) * -1);
    margin-bottom:calc(var(--after-gap) * -1);
    padding:10px var(--pad) var(--after-gap); background:var(--paper); }
  /* The whole bar, background and fade included, is absent until there is
     something in it — an unanswered question would otherwise carry an opaque
     shelf and a gradient across the foot of the screen for no reason. */
  .quiz .after:not(:has(> :not([hidden]))) { display:none; }
  /* The scroll affordance. Content passing UNDER an opaque bar looks cut off;
     content passing under a fade looks like it continues, which here it does —
     the rest of the choices are still down there to be scrolled through. */
  .quiz .after::before { content:''; position:absolute; left:0; right:0;
    bottom:100%; height:24px; pointer-events:none;
    background:linear-gradient(to top, var(--paper), transparent); }
  .quiz .after .next { margin-top:0; flex:1; min-width:0; width:auto; }
  /* Undo and Compare are discs on the LEFT, where a right-handed thumb travels
     to "next question" without passing over either. Undo especially: it is by
     a wide margin the rarer of the two, and a wide control beside Next would
     put the thumb one pixel from throwing the answer away.
  
     Wordless because the row is one row now and the words were what made it
     three. Both carry a title and an aria-label, and Compare's glyph gets its
     introduction on the species page, where the same button is spelled out. */
  .quiz .after .undo, .quiz .after .compare {
    flex:none; width:46px; height:46px; padding:0; gap:0; border-radius:999px;
    background:var(--card); border:1.5px solid var(--hairline-strong);
    color:var(--ink-soft); }
  .quiz .after .undo:hover, .quiz .after .undo:focus-visible,
  .quiz .after .compare:hover, .quiz .after .compare:focus-visible {
    color:var(--ink); }
  .quiz .after .undo[hidden], .quiz .after .compare[hidden] { display:none; }
  /* Several things here are shown by clearing the hidden property, and any
     author display: beats the UA rule — the trap the Library's filter fell
     into. */
  .quiz [hidden] { display:none; }
  /* ---- "I don't know" -----------------------------------------------------
     Under the choices rather than among them, and quiet: it is not a fifth
     bird, and a row that looked like one would be pressed by accident by
     someone answering at speed. It goes away the moment the question is
     answered — there is nothing to admit once the answer is on screen. */
  .quiz .dontknow { align-self:center; margin-top:2px; padding:8px 14px;
    color:var(--ink-soft); font-size:13px; letter-spacing:.02em;
    text-decoration:underline; text-underline-offset:3px; }
  .quiz .dontknow:hover, .quiz .dontknow:focus-visible { color:var(--ink); }
  /* ---- The keyboard legend ------------------------------------------------
     Every one of these keys has worked for months with nothing on screen
     saying so. Desktop only (see keyLegend): a phone has no keys, and its quiz
     screen has no row to spare — which is why the badges moved onto the header
     row in the first place. */
  .quiz .keylegend { display:flex; flex-wrap:wrap; justify-content:center;
    gap:4px 10px; margin-top:2px; color:var(--ink-soft); font-size:11px;
    letter-spacing:.06em; }
  .quiz .keylegend kbd { font-family:inherit; font-size:10.5px;
    border:1.5px solid var(--hairline-strong); border-radius:5px;
    padding:1px 5px; margin-right:4px; color:var(--ink); }
  /* ---- The choice row ----------------------------------------------------
     A wrapper around the choice button, so something can sit beside it. It has
     to be a SIBLING of the button rather than a child: answering disables every
     choice, and a disabled button swallows every pointer event inside it, so a
     control nested in one can never be clicked again. */
  .quiz .choicerow { position:relative; display:flex; }
  .quiz .choicerow > .choice { flex:1; min-width:0; }
  /* Sound ID reserves the gutter on every row, answered or not. Widening it
     only on the row that grew a speaker would re-wrap that row's name under the
     learner's finger, and the rows below it would step down to make room. */
  /* 74px is measured, not chosen: it is the widest gutter that still fits the
     region's long names on one line at 375px — one more name wraps at 76. */
  .quiz.sound .choice { padding-right:74px; }
  .quiz.sound .choice.wrong::after { right:42px; width:24px; height:24px; }
  /* A speaker beside every bird that ISN'T the answer, once the question is
     over: hear what you named, and hear what you didn't, in the same voice you
     were just played. A disc rather than a bare glyph, because on the red row it
     has to read as a control. */
  .quiz .hearit { position:absolute; right:8px; top:50%; transform:translateY(-50%);
    width:30px; height:30px; padding:0; display:grid; place-items:center;
    border-radius:999px; background:var(--overlay-strong); color:var(--on-danger);
    border:1.5px solid transparent; }
  .quiz .hearit:disabled { opacity:.38; }
  /* Sounding: the disc inverts, so it is obvious which of the two birds you are
     listening to. */
  .quiz .hearit.on { background:var(--on-danger); color:var(--danger); }
  /* The rows you did not pick. Greyed to sit with them rather than compete —
     they are an offer to compare, not the result of anything — but a ring and a
     tap target all the same, and it fills in when it is the one sounding. */
  .quiz .hearit.quiet { background:transparent; color:var(--ink-soft);
    border-color:var(--hairline-strong); opacity:.72; }
  .quiz .hearit.quiet:hover { opacity:1; }
  .quiz .hearit.quiet.on { background:var(--moss); color:var(--on-dark);
    border-color:transparent; opacity:1; }
  /* ---- The prompt photo --------------------------------------------------
     The same hand-cut frame the species page gives its hero, and shared because
     two modes now show one: Photo ID's Photo -> Name direction, and the Field
     Test's photo questions. The credit belongs to the picture above it, so it
     tucks up under the frame rather than floating in the middle of the stack
     gap — and it names the photographer, never the bird, so it is safe to show
     before the answer.

     The frame takes the PHOTOGRAPH's shape rather than imposing 4:3 on it: with
     nothing cropped any more (see species-photo.ts), a fixed frame meant a band
     of empty linen above and below every portrait shot. The --photo-ratio is set
     by the frame itself once the image has loaded; the 4:3 is only what holds
     the space until then. The hand-cut radius clips the picture's own corners,
     which is the trade — a sliver of feather for a frame that fits. */
  .quiz .hero { width:100%; aspect-ratio:var(--photo-ratio, 4/3);
    max-height:min(58vh, 460px); border-radius:var(--radius-hand);
    border:1.5px solid var(--hairline); }
  .quiz .attrib { text-align:center; margin:-13px 0 -11px; }
  /* ---- The setup screen --------------------------------------------------
     Every mode opens on one: what it is about to ask, and how much of it. The
     rules are shared because all three screens are the same screen — a mode
     that wants its own accent on the chosen option sets --seg-on and stops
     there. */
  .quiz .setup { display:flex; flex-direction:column; gap:18px; }
  /* The two slots a focused round fills — its note, and the length row it
     replaces. Held open so the voices control can swap them in place rather
     than repainting the screen and dropping focus off the button just pressed,
     and hidden when empty so an ordinary round is not paying a row's gap for a
     slot it never uses. */
  .quiz .setup .focusslot:empty { display:none; }
  .quiz .setup h1 { font-size:32px; margin:0; }
  .quiz .setup .blurb { line-height:1.55; color:var(--ink-soft); margin:-14px 0 0; }
  .quiz .field { display:flex; flex-direction:column; gap:8px; }
  .quiz .seg { display:flex; flex-direction:column; gap:8px; }
  .quiz .seg button { text-align:left; justify-content:flex-start; flex-direction:column;
    align-items:flex-start; gap:3px; padding:13px 16px; font-size:16px; }
  .quiz .seg button .note { display:block; font-family:var(--font-body);
    font-size:12px; letter-spacing:0; color:var(--ink-soft); text-transform:none; }
  .quiz .seg button.on, .quiz .lens button.on { background:var(--seg-on, var(--moss));
    color:var(--on-dark); border-color:var(--honey-soft); }
  .quiz .seg button.on .note { color:var(--on-dark-soft); }
  /* Round length: a row of numbers rather than a stack, because the options are
     one glyph each and reading them as a scale is the whole point — and the
     label sits BESIDE them for the same reason. Name Match's setup carries five
     option rows on top of this one, and a heading on its own line over four
     numerals was the difference between Start being on the screen and Start
     being under the nav bar. */
  .quiz .lenfield { flex-direction:row; align-items:center; gap:12px; }
  .quiz .lenfield .lens { flex:1; min-width:0; }
  .quiz .lens { display:flex; gap:7px; }
  .quiz .lens button { flex:1; padding:13px 0; font-size:17px;
    font-variant-numeric:tabular-nums; }
  /* Four words where the row above has four numerals: smaller, and allowed to
     lose their letter-spacing before they wrap, because "Many" wrapping to two
     lines would make this row taller than the one it belongs with. */
  .quiz .newfield .lens button { font-size:12.5px; letter-spacing:.04em; }
  .quiz .done { text-align:center; padding:40px 0; }
  .quiz .done .big { font-family:var(--font-display); font-variation-settings:var(--fvs-display);
    font-size:30px; }
  .quiz .done .stat { color:var(--ink-soft); margin-top:8px; }
  /* A finished round offers another one, so the done panel is a stack of
     buttons rather than the single full-width bar a .next is elsewhere. */
  .quiz .done .next { display:flex; width:100%; max-width:280px; margin:14px auto 0; }
  /* ---- The misses ---------------------------------------------------------
     "7/10" is a score, not a lesson. These are the three birds the round was
     actually about, each a tap from its own page. */
  .quiz .done .missed { max-width:320px; margin:26px auto 0; text-align:left;
    display:flex; flex-direction:column; }
  .quiz .done .missed > .label { margin-bottom:2px; }
  .quiz .done .missrow { display:flex; align-items:center; gap:10px; padding:10px 2px;
    border-bottom:1.5px solid var(--hairline); color:var(--ink); }
  /* The row is the link PLUS its speaker, so the link is the part that fills it.
     Its own flex, because the names and the chevron used to be the row's. */
  .quiz .done .misslink { flex:1; min-width:0; display:flex; align-items:center;
    gap:10px; color:var(--ink); text-decoration:none; }
  .quiz .done .missrow:last-child { border-bottom:none; }
  /* The rows the list is holding back. Spelled out at this weight because the
     row's own display:flex outranks the general hidden rule above. */
  .quiz .done .missrow[hidden] { display:none; }
  .quiz .done .missrow .names { flex:1; min-width:0; }
  .quiz .done .missrow .nm { font-family:var(--font-display); font-size:16.5px;
    line-height:1.2; display:block; }
  .quiz .done .missrow .sci { font-style:italic; font-size:12.5px; color:var(--ink-soft);
    display:block; }
  .quiz .done .missrow .icon { flex:none; color:var(--ink-soft); }
  /* The speaker on a miss row. Same control as the one beside a wrong choice,
     but this list is a flex column rather than a stack of positioned rows, so
     it sits IN the row instead of over it — and it sits outside the <a>, which
     is a link to the species page and would otherwise swallow the tap. Quiet by
     default: the row is a record of a miss, not an offer of a verdict. */
  .quiz .done .missrow .hearit { position:static; transform:none; flex:none;
    background:transparent; color:var(--ink-soft);
    border-color:var(--hairline-strong); }
  .quiz .done .missrow .hearit.on { background:var(--moss); color:var(--on-dark);
    border-color:transparent; }
  .quiz .done .missed .more { align-self:center; margin-top:8px; color:var(--ink-soft);
    font-size:11px; letter-spacing:.1em; text-transform:uppercase;
    text-decoration:underline; text-underline-offset:3px; }
  /* The sound type, revealed with the answer: in a normal round the learner has
     no way of knowing whether they were placing a song or a call, and the
     difference is most of what makes one hard. */
  .quiz .typetag { color:var(--ink-soft); border:1.5px solid var(--hairline-strong);
    border-radius:999px; padding:3px 8px; font-size:8.6px; letter-spacing:.1em;
    line-height:1; flex:none; }
  /* Desktop windows are wide but often short — a phone-sized tap target down a
     four-choice list pushed the Next button past the fold on anything that
     wasn't full-screen. Reclaim the height once there's width to spare; touch
     targets are unaffected because phones never reach this breakpoint. */
  @media (min-width:700px) {
    .quiz { gap:16px; }
    /* On a desktop window the 4:3 hero is the single tallest thing on the page
       and it, plus four choices, spilled past the fold on anything short of
       full-screen. Trade a little photo for a question that fits. */
    /* Height-led rather than width-led: the box keeps the photo's ratio and
       shrinks to the height budget, so trading photo for question costs a band
       of nothing rather than adding one. */
    .quiz .hero { width:auto; max-width:100%; height:min(38vh, 340px);
      max-height:none; margin:0 auto; }
    .quiz .choices { gap:8px; margin-top:4px; }
    .quiz .choice { padding:11px 52px 11px 16px; font-size:17px; }
    .quiz .choice .sci { font-size:12px; margin-top:1px; }
    .quiz .next { padding:12px; }
  }
  /* The short-phone pass, and the one that decides whether Next is reachable.
     A 667pt screen (SE, or a tall phone with the URL bar down) has ~90px less
     to spend than the tall one everything was measured on. The 70vh floor goes
     first — it exists to push Next to the bottom of a screen with room to
     spare, and here there is none — then the frame's own padding and gaps. */
  @media (max-height:740px) {
    .quiz { --after-gap:10px; gap:13px;
      padding:4px var(--pad) var(--after-gap); min-height:0; }
    /* Short phones (an iPhone SE, or a tall one with the URL bar showing) can't
       spend 250px on a 4:3 hero and still show the answer list. Cap it by
       height instead — the frame stays hand-cut, it just crops less tall. */
    .quiz .hero { width:auto; max-width:100%; height:min(28vh, 190px);
      max-height:none; margin:0 auto; }
    .quiz .choices { gap:8px; margin-top:0; }
    .quiz .next { padding:13px; }
    /* The setup screen grew a fourth thing to choose, and on a 667pt screen
       Name Match's three fields plus the length then put Start under the nav
       bar. Same treatment as the question screens: spend the gaps first, then
       the padding, and leave the tap targets alone. */
    .quiz .setup { gap:14px; }
    .quiz .setup h1 { font-size:27px; }
    .quiz .setup .blurb { margin-top:-8px; }
    .quiz .seg { gap:6px; }
    .quiz .seg button { padding:10px 14px; }
    .quiz .lens button { padding:11px 0; }
    .quiz .lenfield { gap:10px; }
  }
`,
  );
}

/**
 * Paint the outcome across a row of name choices: the pick, the answer it
 * missed if the pick was wrong, and everything else dimmed. `codes` is parallel
 * to `buttons`; `picked` is the index the user chose.
 */
export function markChoices(
  buttons: HTMLButtonElement[],
  codes: string[],
  picked: number,
  targetCode: string,
): void {
  const gotIt = codes[picked] === targetCode;
  buttons.forEach((b, i) => {
    b.disabled = true;
    if (i === picked) b.classList.add(gotIt ? 'correct' : 'wrong');
    else if (codes[i] === targetCode) b.classList.add('answer');
    else b.classList.add('dim');
  });
}

/** The back arrow that leaves a quiz. */
/**
 * The "Undo" under a question that has just been answered.
 *
 * Built here rather than five times over because the wording, the icon and the
 * disabled-while-it-runs guard are the same in every mode — and because the one
 * thing a view must not do is offer it twice. `run` is awaited, and the button
 * is dead for the duration: undoing an answer touches the card store, the review
 * log and the XP total, and a double press racing itself through those is how a
 * round ends up crediting a bird it took back.
 */
export function undoButton(run: () => Promise<void>): HTMLButtonElement {
  const btn = el(
    'button.undo.btn',
    {
      type: 'button',
      title: t('quiz.chrome.undo.title'),
      'aria-label': t('quiz.chrome.undo.label'),
    },
    icon('undo', { size: 18 }),
  ) as HTMLButtonElement;
  btn.hidden = true;
  btn.addEventListener('click', () => {
    if (btn.disabled) return;
    btn.disabled = true;
    void run().finally(() => {
      btn.disabled = false;
    });
  });
  return btn;
}

/**
 * "I don't know" — the answer a four-choice list could not otherwise give.
 *
 * Without it the only way past a bird you cannot name is to guess, and a blind
 * one-in-four that lands is graded `good`: the scheduler is told you knew it and
 * puts it away for weeks. That is the one wrong answer this app was incapable of
 * hearing, and it is the commonest thing a learner actually wants to say.
 *
 * Graded `again` with no XP, and — the part that matters beyond the schedule —
 * with no `picked`, so it never reaches the mix-up record. Naming no bird is not
 * confusing two of them, and a drill built out of skips would be a drill about
 * nothing.
 *
 * Built here rather than five times over, for the reason `undoButton` is: the
 * wording and the disabled-while-it-runs guard are the same in every mode, and
 * grading twice through a double press is the one thing it must not do.
 */
export function dontKnowButton(run: () => Promise<void>): HTMLButtonElement {
  const btn = el(
    'button.dontknow.btn.btn-plain',
    {
      type: 'button',
      title: t('quiz.chrome.dontKnow.title'),
    },
    t('quiz.chrome.dontKnow.label'),
  ) as HTMLButtonElement;
  btn.addEventListener('click', () => {
    if (btn.disabled) return;
    btn.disabled = true;
    void run();
  });
  return btn;
}

/**
 * Undo, Compare and Next as ONE row pinned to the foot of the screen.
 *
 * The order is the order of consequence: the two discs that take something
 * back or open something sideways, then the wide button that commits. See the
 * .after block in the stylesheet for why it is pinned and why it is one row.
 */
export function afterBar(
  undo: HTMLButtonElement,
  next: HTMLButtonElement,
  compare?: HTMLButtonElement,
): HTMLElement {
  return el('div.after', {}, undo, compare, next);
}

/** A "Compare" that knows which two birds it is about. */
export interface CompareOffer {
  element: HTMLButtonElement;
  /** Show it, pointing at these two birds. */
  offer: (left: string, right: string, facet?: Facet) => void;
}

/**
 * The "Compare" under an answered question: the bird you were asked about,
 * held against the bird you named instead.
 *
 * This is the moment the side-by-side view exists for. A wrong answer in this
 * app is almost never a guess — it is two birds a learner cannot yet tell
 * apart — and the round has just shown one of them and had the other named
 * out loud. Anything later is too late: by the end of the round the pair has
 * become a row in a Missed list, and by the Library it has become a memory.
 *
 * Offered ONLY when the pick differs from the answer, because on a right
 * answer there is no second bird — the three you rejected already have their
 * own speakers beside them (see hearOthers), which is the comparison a correct
 * answer actually wants.
 *
 * It opens the surface as an OVERLAY rather than navigating. A round is state
 * in its view's closure — the questions, the tally, the answer still standing
 * undone — and the router tears a view down on navigation, so a "Compare" that
 * routed would answer the learner's question by throwing away the round that
 * raised it.
 */
export function compareButton(): CompareOffer {
  const btn = el(
    'button.compare.btn',
    { type: 'button', title: t('quiz.chrome.compare.title') },
    icon('compare', { size: 18 }),
  ) as HTMLButtonElement;
  btn.hidden = true;
  let pair: { left: string; right: string; facet?: Facet } | null = null;
  btn.addEventListener('click', () => {
    if (pair) openCompare(pair.left, pair.right, { facet: pair.facet });
  });
  return {
    element: btn,
    offer: (left, right, facet) => {
      // Same bird both sides is not a comparison. Modes call this
      // unconditionally on an answer and let it decide, so the rule lives in
      // one place rather than in five.
      if (left === right) return;
      pair = { left, right, facet };
      btn.hidden = false;
      btn.setAttribute('aria-label', t('quiz.chrome.compare.label'));
    },
  };
}

/**
 * The way out of a finished round.
 *
 * An ordinary round goes Home, which is where it was started from. A focused
 * one steps BACK instead, because the screen it came from is holding something
 * worth returning to — a Library filtered by hand, or the Home card that named
 * the pool — and Home would throw the first of those away.
 *
 * The label follows `from` rather than assuming the Library. A focused round is
 * reachable from both screens now, and a button that says "Back to the Library"
 * and lands on Home is worse than one that says nothing.
 */
export function exitButton(ctx: ViewContext, focused: boolean): HTMLElement {
  const toLibrary = focused && ctx.from.startsWith('/library');
  if (!focused) {
    return el(
      'button.next.btn.btn-quiet',
      { onclick: () => ctx.navigate('/home') },
      t('quiz.chrome.exit.home'),
    );
  }
  return el(
    'button.next.btn.btn-quiet',
    { onclick: () => ctx.back(toLibrary ? '/library' : '/home') },
    toLibrary ? t('quiz.chrome.exit.library') : t('quiz.chrome.exit.home'),
  );
}

export function backBtn(ctx: ViewContext): HTMLElement {
  return el(
    'button.back.btn.btn-plain',
    { onclick: () => ctx.navigate('/home'), 'aria-label': t('quiz.chrome.exit.home') },
    icon('arrow-left', { size: 22 }),
  );
}

/**
 * Fill the header's progress bar to include the question just answered.
 *
 * The bar is drawn from `index`, which is how far the round has got — right
 * while the question is open, and one short the moment it is answered. Left
 * alone it meant the last question of a round of ten sat at 90% while its
 * result was on screen, so a finished round never once looked finished.
 */
export function fillProgress(header: HTMLElement, index: number, total: number): void {
  const bar = header.querySelector<HTMLElement>('.progress > i');
  if (bar) bar.style.width = `${((index + 1) / Math.max(1, total)) * 100}%`;
}

/**
 * Back button, question, and counter on one row, with the progress bar beneath —
 * so the question is the first thing read, not something below the chrome.
 */
export function quizHeader(
  ctx: ViewContext,
  index: number,
  total: number,
  prompt: string,
): HTMLElement {
  return el(
    'div.qhead',
    {},
    el(
      'div.bar',
      {},
      backBtn(ctx),
      el('div.prompt.label', {}, prompt),
      el('div.tags', {}),
      el('div.count', {}, t('quiz.chrome.header.count', { n: index + 1, total })),
    ),
    el('div.progress.meter', {}, el('i', { style: { width: `${(index / total) * 100}%` } })),
  );
}

/**
 * Where a mode hangs its badges: the skill a Field Test question turned out to
 * be, whether calls are in the mix, which voice a clip was.
 *
 * Read back off the header rather than passed in, because half of these are
 * only shown once the question is ANSWERED — the sound type names half the
 * answer before the answer is in — so the mode has to reach the slot again
 * later. Same reasoning, and the same lookup, as fillProgress above.
 */
export function tagSlot(header: HTMLElement): HTMLElement {
  return header.querySelector<HTMLElement>('.tags')!;
}

/**
 * What the keyboard can reach on the screen as it stands right now.
 *
 * Read through a getter rather than registered per question: a round re-renders
 * for every answer, and a listener added on each render is a listener leaked on
 * each render.
 */
export interface KeyActions {
  /** Answer buttons in display order; the digit keys press them. */
  choices?: HTMLButtonElement[];
  /** Space — replay the recording, flip the card. */
  replay?: () => void;
  /** Enter — the button that moves the round on. */
  next?: HTMLButtonElement | null;
  /** Backspace — take the answer just given back. */
  undo?: HTMLButtonElement | null;
  /** Zero — the key beside the choices that means none of them. */
  skip?: HTMLButtonElement | null;
}

/**
 * Digits answer, Space replays, Enter advances. Returns the disposer, which the
 * view must call from its teardown.
 *
 * Space and Enter stand back whenever a real control has focus, so tabbing to
 * the back arrow and pressing Enter still goes back rather than answering the
 * question behind it. Digits do not stand back — nothing else in a quiz wants
 * them — except in a text field, where they are the learner typing.
 *
 * `root` is the view's own element, and the handler does nothing once it has
 * left the document. The disposer is the ordinary way this listener ends, but a
 * view that is navigated away from while it is still loading never gets to
 * return one — and a keystroke landing on the screen after it would otherwise
 * press a button in a detached round and grade a card the learner cannot see.
 */
export function quizKeys(root: HTMLElement, current: () => KeyActions | null): () => void {
  const onKey = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
    if (!root.isConnected) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
    const actions = current();
    if (!actions) return;

    if (e.key >= '1' && e.key <= '9') {
      const btn = actions.choices?.[Number(e.key) - 1];
      if (!btn || btn.disabled || btn.hidden) return;
      e.preventDefault();
      btn.click();
      return;
    }
    // Zero, because 1-4 are the birds and 0 is the key beside them that names
    // none of them. Before the stand-back check below for the same reason the
    // digits are: nothing else in a quiz wants it.
    if (e.key === '0') {
      const btn = actions.skip;
      if (!btn || btn.disabled || btn.hidden) return;
      e.preventDefault();
      btn.click();
      return;
    }

    // A focused button already answers to both of these; hijacking them would
    // either double-fire or fire the wrong control.
    const onControl =
      !!active &&
      (active.tagName === 'BUTTON' || active.tagName === 'A') &&
      !(active as HTMLButtonElement).disabled;
    if (onControl) return;

    if (e.key === ' ' || e.key === 'Spacebar') {
      if (!actions.replay) return;
      e.preventDefault(); // the page would scroll
      actions.replay();
      return;
    }
    if (e.key === 'Enter') {
      const btn = actions.next;
      if (!btn || btn.hidden || btn.disabled) return;
      e.preventDefault();
      btn.click();
      return;
    }
    // Backspace, because on a keyboard the answer was a digit and Backspace is
    // already the key that deletes the last thing typed. It has nothing else to
    // do here: this handler has already stood down inside a text field.
    if (e.key === 'Backspace') {
      const btn = actions.undo;
      if (!btn || btn.hidden || btn.disabled) return;
      e.preventDefault();
      btn.click();
    }
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}

/**
 * Whether this device is likely to have a keyboard at all.
 *
 * A guess, and the only one available: the web cannot ask. `hover` and a fine
 * pointer together mean a mouse or a trackpad, which in practice means a
 * machine with keys — a phone matches neither, and a tablet with a keyboard
 * case matches both, which is the right answer for it too.
 */
function hasKeyboard(): boolean {
  if (typeof matchMedia !== 'function') return false;
  return matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * The keys this screen answers to, said out loud.
 *
 * Digits, Space, Enter and Backspace have all worked for months with nothing
 * anywhere saying so, which makes them features for the person who wrote them.
 * Built from the actions the mode actually passed, so a screen without a
 * recording never claims Space and the flip card never claims digits.
 *
 * Empty and hidden on a touch device: there are no keys to press, and the quiz
 * screen is the one place in this app with no row to spare — the badges moved
 * onto the header row to win back three of them. Hidden rather than null so
 * that every mode can append it unconditionally; `display:none` takes the
 * flex gap with it, so an absent legend costs nothing on screen either.
 */
export function keyLegend(actions: KeyActions): HTMLElement {
  const row = el('div.keylegend', {});
  if (!hasKeyboard()) {
    row.hidden = true;
    return row;
  }
  const keys: [string, string][] = [];
  const n = actions.choices?.length ?? 0;
  if (n) {
    keys.push([
      n > 1 ? t('quiz.chrome.keys.choiceRange', { n }) : '1',
      t('quiz.chrome.keys.answer'),
    ]);
  }
  if (actions.skip) keys.push(['0', t('quiz.chrome.keys.skip')]);
  if (actions.replay) keys.push([t('quiz.chrome.keys.space'), t('quiz.chrome.keys.replay')]);
  if (actions.next) keys.push([t('quiz.chrome.keys.enter'), t('quiz.chrome.keys.next')]);
  if (actions.undo) keys.push(['⌫', t('quiz.chrome.keys.undo')]);
  row.hidden = !keys.length;
  row.append(...keys.map(([key, what]) => el('span', {}, el('kbd', {}, key), what)));
  return row;
}

/**
 * What a scientific name means, in the two halves it is made of.
 *
 * "Spatula = spoon · cyanoptera = blue-winged" — the thing that turns a
 * binomial from a string to memorise into a description of the bird in front of
 * you. Callers must only ever mount it AFTER the answer is in: half of these
 * glosses name the bird outright, and "blue-winged" over four teal is not a
 * question any more.
 *
 * Null when the pack carries no gloss for this name. The etymology step needs
 * an API key the app never sees (see tools/pipeline/lib/etymology.ts), so a
 * missing line is the ordinary state of a name nobody has run it for — not an
 * error, and not something to leave a gap for.
 */
export function etymologyLine(sp: Species | undefined): HTMLElement | null {
  if (!sp?.etym) return null;
  const [genus, ...rest] = sp.sci.split(/\s+/);
  const epithet = rest.join(' ');
  return el(
    'div.etym',
    { title: t('common.etymTitle', { sci: sp.sci }) },
    ...tParts('quiz.chrome.etym.line', {
      genus,
      genusMeaning: sp.etym.genus,
      epithet,
      epithetMeaning: sp.etym.epithet,
      word: (c) => el('span.word', {}, ...c),
      sep: (c) => el('span.sep', {}, ...c),
    }),
  );
}

/** One bird a round got wrong, and the voice it was asked in. */
export interface Miss {
  code: string;
  /**
   * The sound type the question played, where one did. A miss in Name Match or
   * Photo ID has no voice of its own and falls back to the song, which is still
   * the thing worth hearing about a bird you have just failed to name.
   */
  type?: SoundType;
}

/** A missed list and the speakers on it, which the view has to be able to stop. */
export interface MissedPanel {
  element: HTMLElement;
  speakers: HearHandle[];
}

/**
 * One row per BIRD, in the order they first went wrong.
 *
 * Nothing else in the app can miss the same species twice in a round — a bird
 * is asked once — but a focused Field Test can: "Drill this bird" asks one
 * species by sound, by sight and by name, and getting all three wrong used to
 * print its name three times under "Missed", which is a list of questions
 * wearing the heading of a list of birds. A voice beats no voice when the same
 * bird comes back, so the row plays what it was actually asked in rather than
 * the song it falls back to.
 */
function oneRowPerBird(misses: readonly Miss[]): Miss[] {
  const seen = new Map<string, Miss>();
  for (const miss of misses) {
    // Copied, not held: the caller's list is the round's own record of what went
    // wrong, and the panel has no business editing it.
    const had = seen.get(miss.code);
    if (!had) seen.set(miss.code, { ...miss });
    else if (!had.type && miss.type) had.type = miss.type;
  }
  return [...seen.values()];
}

/**
 * The birds a finished round got wrong: each one playable where it stands, and
 * each a tap from its own page.
 *
 * The panel used to be links and nothing else, so hearing the bird you had just
 * missed meant leaving the scorecard to do it — and a scorecard you have walked
 * away from is one you do not come back to. The clip is the whole content of a
 * sound miss, and it costs a row control rather than a navigation.
 *
 * Null for a clean round, so the panel simply isn't there rather than being an
 * empty heading over nothing. `label` is the heading, for a screen whose list is
 * not strictly misses — Birdle's is the birds that were guessed instead.
 */
export function missedList(
  misses: readonly Miss[],
  ctx: ViewContext,
  label = t('quiz.chrome.missed.label'),
): MissedPanel | null {
  if (!misses.length) return null;
  const speakers: HearHandle[] = [];
  const rows = oneRowPerBird(misses).map(({ code, type }) => {
    const speaker = hearIt(code, type ?? 'song');
    speakers.push(speaker);
    return speciesRow(code, ctx, speaker);
  });
  const panel = el('div.missed', {}, el('span.label', {}, label), ...rows);

  // A bad round can miss all ten, and ten rows push the buttons under the fold
  // on a short phone — so the list opens at five and says how many it is
  // holding back. Nothing is dropped: the count is part of the offer.
  const extra = rows.slice(MISSED_SHOWN);
  if (extra.length) {
    for (const row of extra) row.hidden = true;
    const more = el(
      'button.more.btn.btn-plain',
      { type: 'button' },
      t('quiz.chrome.missed.more', { n: extra.length }),
    );
    more.addEventListener('click', () => {
      for (const row of extra) row.hidden = false;
      more.remove();
    });
    panel.append(more);
  }
  return { element: panel, speakers };
}

/** How many misses a done panel lists before it starts counting the rest. */
const MISSED_SHOWN = 5;

/**
 * One bird as a row on a done panel: both its names, a link to its page, and
 * its speaker beside it when it has one. Goes inside a `.missed` panel, which
 * is where its styles live.
 */
export function speciesRow(code: string, ctx: ViewContext, speaker?: HearHandle): HTMLElement {
  const sp = getSpecies(code);
  // A real href, so the row is a link to long-press, copy or open — the click
  // handler only exists because the router owns navigation.
  const link = el(
    'a.misslink',
    { href: `#/species/${code}` },
    el(
      'span.names',
      {},
      el('span.nm', {}, sp?.common ?? code),
      el('span.sci', {}, sp?.sci ?? ''),
    ),
    icon('arrow-right', { size: 16 }),
  );
  link.addEventListener('click', (e) => {
    e.preventDefault();
    ctx.navigate(`/species/${code}`);
  });
  // Beside the link, never inside it: a control nested in an <a> is a tap that
  // navigates as well as plays. Same reason the choice rows keep their speaker
  // a sibling of the button.
  return el('div.missrow', {}, link, speaker?.element ?? null);
}

/** A speaker button and the means to silence it. */
export interface HearHandle {
  element: HTMLButtonElement;
  stop: () => void;
}

/**
 * Play one species in one voice — the bird a Sound ID answer named by mistake,
 * in the same sound type the question played.
 *
 * The comparison IS the lesson: the round has just played a bird, the learner
 * has just named a different one, and the two clips side by side are the only
 * thing that explains the mistake. Same type deliberately — a species' call
 * against another's song compares nothing, so a bird with no recording of the
 * type comes back as a disabled button that says why rather than as a
 * substitution the learner would have no way of noticing.
 *
 * Nothing plays until it is asked for. The clip that was the question is still
 * on screen and still tappable, and starting a second bird unbidden would talk
 * over the learner deciding whether they want it.
 */
export function hearIt(
  code: string,
  type: SoundType,
  rnd: () => number = Math.random,
): HearHandle {
  const sp = getSpecies(code);
  const pool = sp?.audio.filter((a) => a.type === type) ?? [];
  const clip: AudioAsset | undefined = pool.length
    ? pool[Math.floor(rnd() * pool.length)]
    : undefined;
  const name = sp?.common ?? code;

  const btn = el(
    'button.hearit.btn.btn-plain',
    { type: 'button' },
    icon('volume', { size: 18 }),
  ) as HTMLButtonElement;

  let playback: Playback | null = null;
  let raf = 0;

  const idle = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    btn.classList.remove('on');
  };
  const stop = () => {
    playback?.stop();
    playback = null;
    idle();
  };

  if (!clip) {
    const why = t('quiz.chrome.hear.missing', { name, type });
    btn.disabled = true;
    btn.title = why;
    btn.setAttribute('aria-label', why);
    return { element: btn, stop };
  }

  const label = t('quiz.chrome.hear.play', { name, type });
  btn.title = label;
  btn.setAttribute('aria-label', label);

  const begin = async (): Promise<void> => {
    btn.classList.add('on');
    let started: Playback;
    try {
      // play() silences whatever else is sounding, the question's own clip
      // included — two birds at once teaches nothing.
      started = await play(clip);
    } catch {
      idle();
      return;
    }
    // The round moved on while the clip was being fetched.
    if (!btn.isConnected) {
      started.stop();
      idle();
      return;
    }
    playback = started;
    const poll = () => {
      if (!playback) return;
      if (!btn.isConnected || playback.progress() >= 1) return stop();
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
  };

  btn.addEventListener('click', () => {
    if (playback) stop();
    else void begin();
  });

  return { element: btn, stop };
}

/**
 * A speaker beside every choice that is not the answer, once the round has
 * moved past the question.
 *
 * It used to appear on one row only, and only when you were wrong: the bird you
 * named, so you could hear what you had confused it with. But the three birds
 * you DIDN'T name are the same offer — you were just played a recording and
 * asked to separate four species, and "what do the other three sound like" is
 * the question the answer screen leaves you with whether you got it right or
 * not. The answer's own row gets none: you have just heard it.
 *
 * The row you picked wrongly keeps the strong disc, because it sits on the red
 * fill; the rest are quiet, greyed to match the rows they belong to.
 */
export function hearOthers(
  rows: HTMLElement[],
  choices: readonly string[],
  target: string,
  type: SoundType,
  picked?: string,
): HearHandle[] {
  const handles: HearHandle[] = [];
  choices.forEach((code, i) => {
    if (code === target || !rows[i]) return;
    const speaker = hearIt(code, type);
    if (code !== picked) speaker.element.classList.add('quiet');
    rows[i].append(speaker.element);
    handles.push(speaker);
  });
  return handles;
}

/**
 * Mark an option chosen, or not.
 *
 * The class is what paints it; `aria-pressed` is what says so out loud. A
 * setup screen is a set of choices already made, and a colour is not one of the
 * things a screen reader can pass on.
 */
function setPicked(btn: HTMLButtonElement, on: boolean): void {
  btn.classList.toggle('on', on);
  btn.setAttribute('aria-pressed', String(on));
}

/**
 * A vertical radio group that repaints its own selection: one option per row,
 * each with a note saying what picking it does.
 */
export function segmented<T extends string>(
  options: { value: T; label: string; note: string }[],
  current: T,
  onPick: (value: T) => void,
): HTMLElement {
  const group = el('div.seg', {});
  const buttons = options.map((o) => {
    const btn = el(
      'button.btn.btn-quiet',
      {},
      o.label,
      el('span.note', {}, o.note),
    ) as HTMLButtonElement;
    setPicked(btn, o.value === current);
    btn.addEventListener('click', () => {
      for (const b of buttons) setPicked(b, false);
      setPicked(btn, true);
      onPick(o.value);
    });
    group.append(btn);
    return btn;
  });
  return group;
}

/**
 * The "how many birds" field: the labelled row of round lengths, already
 * wired to repaint itself.
 *
 * Shared by all three setup screens because the question is the same one in
 * each, and because the number a learner picks here is the only thing that
 * decides how long they are about to be sitting.
 */
export function lengthField(
  current: number,
  onPick: (len: number) => void,
  lengths: readonly number[] = ROUND_LENGTHS,
): HTMLElement {
  const row = el('div.lens', {});
  const buttons = lengths.map((n) => {
    const btn = el('button.btn.btn-quiet', {
      type: 'button',
      'aria-label': t('common.birds', { n }),
    }) as HTMLButtonElement;
    btn.textContent = String(n);
    setPicked(btn, n === current);
    btn.addEventListener('click', () => {
      for (const b of buttons) setPicked(b, false);
      setPicked(btn, true);
      onPick(n);
    });
    row.append(btn);
    return btn;
  });
  return el('div.field.lenfield', {}, el('div.prompt.label', {}, t('quiz.chrome.length.label')), row);
}

/**
 * What each share of the round reads as on the button, and what it means said
 * out loud. Parallel to `NEW_SHARES`, and checked against it at build time
 * below — a fifth share with no word for it would otherwise ship as a blank
 * button.
 */
const SHARE_WORDS: Record<string, { label: string; said: string }> = {
  '0': { label: t('quiz.chrome.newBirds.none'), said: t('quiz.chrome.newBirds.noneSaid') },
  '0.3': { label: t('quiz.chrome.newBirds.few'), said: t('quiz.chrome.newBirds.fewSaid') },
  '0.6': { label: t('quiz.chrome.newBirds.many'), said: t('quiz.chrome.newBirds.manySaid') },
  '1': { label: t('quiz.chrome.newBirds.all'), said: t('quiz.chrome.newBirds.allSaid') },
};

/**
 * The "new birds" field: how much of the round is spent meeting species the
 * learner has never seen, rather than revising the ones they have.
 *
 * A row of four words beside its label, like the lengths above it and directly
 * under them, because it is the second half of the same question — how long,
 * and how much of it is new. Words rather than the counts they work out to,
 * even though the counts are what a learner feels, because two rows of numerals
 * one above the other read as one scale split in half rather than as two
 * separate choices; the counts are in the spoken labels, where they cost no
 * width.
 */
export function newBirdsField(current: number, onPick: (share: number) => void): HTMLElement {
  const row = el('div.lens', {});
  const buttons = NEW_SHARES.map((share) => {
    const words = SHARE_WORDS[String(share)] ?? { label: `${share}`, said: `${share}` };
    const btn = el('button.btn.btn-quiet', {
      type: 'button',
      'aria-label': words.said,
      title: words.said,
    }) as HTMLButtonElement;
    btn.textContent = words.label;
    setPicked(btn, share === current);
    btn.addEventListener('click', () => {
      for (const b of buttons) setPicked(b, false);
      setPicked(btn, true);
      onPick(share);
    });
    row.append(btn);
    return btn;
  });
  return el('div.field.lenfield.newfield', {}, el('div.prompt.label', {}, t('quiz.chrome.newBirds.label')), row);
}

// ---- Focused rounds --------------------------------------------------------
//
// A round started from the Library is confined to the birds that were on screen
// when the button was pressed. Three things follow, and all five modes need all
// three, so they live here rather than five times over: the setup screen has to
// say what the round is, the round itself has to keep saying it, and the length
// on offer can no longer be the fixed ladder.

/**
 * The round lengths a focused round can honestly offer.
 *
 * A round is never longer than its pool — a species is asked at most once —
 * so a focus of nine birds offering twenty would be promising eleven questions
 * that cannot exist. Merely greying out the rungs above nine is not enough
 * either, because then no rung says nine: the ladder is cut short and the pool
 * size is added as its top step. Three birds is a round of three, with nothing
 * to choose.
 */
export function focusLengths(pool: number): number[] {
  const capped = Math.min(pool, ROUND_LENGTHS[ROUND_LENGTHS.length - 1]);
  return [...ROUND_LENGTHS.filter((n) => n < capped), capped];
}

/** The line above a focused setup screen, saying what this round is. */
export function focusNote(focus: Focus, n: number): HTMLElement {
  return el('p.blurb.meta', {}, t('quiz.chrome.focus.note', { focus: focusLabel(focus, n) }));
}

/**
 * The badge a focused round carries on every question.
 *
 * In the same slot and the same shape as "Mix-ups" and "With calls", because it
 * is the same kind of fact: this round is not the ordinary one, and a learner
 * who came back to it after a phone call should not have to remember that.
 */
export function focusTag(focus: Focus, n: number): HTMLElement {
  const words = focusLabel(focus, n);
  return el(
    'span.hardtag.label',
    { title: t('quiz.chrome.focus.tagTitle', { focus: words.toLowerCase() }) },
    focus.kind === 'family'
      ? focus.family!
      : focus.kind === 'trait' && focus.trait
        ? traitValueLabel(focus.trait.axis, focus.trait.value)
        : t('common.birds', { n }),
  );
}

/**
 * The header's mark on a question that is back because it was missed — see
 * `retryMisses` in core/quiz/settings.ts. It says why the bird is familiar, and
 * that this answer is not the one being scored.
 */
export function retryTag(): HTMLElement {
  return el('span.hardtag.label', { title: t('quiz.chrome.retry.tagTitle') }, t('quiz.chrome.retry.tag'));
}

/** What a focus does to a setup screen. All five modes ask the same questions. */
export interface FocusSetup {
  /** The line that says what this round is, or null for an ordinary round. */
  note: HTMLElement | null;
  /** The lengths this round may offer. */
  lengths: readonly number[];
  /** The caller's `len`, clamped into `lengths`. */
  len: number;
  /** Why there is nothing to study, or null when there is. */
  empty: string | null;
}

/**
 * Work out the setup screen for a focused round, in one place, because the
 * three answers are the same in all five modes and the wrong version of any of
 * them is a promise the round then breaks.
 *
 * `askable` is the focus already intersected with what this mode can ask about,
 * which is not the same number: nine sparrows is seven questions in Photo ID if
 * two of them have no photograph. `lacking` is the sentence saying what the
 * missing ones are missing, for the case where the focus is real but this mode
 * can do nothing with it.
 *
 * `reach` is how many QUESTIONS the focus comes to, where that is more than the
 * number of birds — the Field Test only, which can ask one bird by sound, by
 * sight and by name in the same round. It raises the length ladder and nothing
 * else: the note still counts birds, so a one-bird drill reads "1 bird" over a
 * round of three rather than claiming to be three birds.
 */
export function focusSetup(
  focus: Focus | null,
  askable: string[] | null,
  len: number,
  lacking: string,
  reach?: number,
): FocusSetup {
  if (!focus || !askable) return { note: null, lengths: ROUND_LENGTHS, len, empty: null };
  if (!askable.length) {
    // Two different failures wearing the same empty list: the focus itself came
    // to nothing, or it did not and this mode has no media for any of it.
    const empty = emptyFocusReason(focus, speciesList()) ?? lacking;
    // The reason IS the note here. Printing "0 birds from the Library" above it
    // would be the screen saying the same thing twice, once uselessly.
    return { note: el('p.blurb.meta', {}, empty), lengths: ROUND_LENGTHS, len, empty };
  }
  const lengths = focusLengths(Math.max(askable.length, reach ?? 0));
  return {
    note: focusNote(focus, askable.length),
    lengths,
    // A remembered twenty is meaningless over nine birds, so the round opens on
    // the longest it can actually be rather than on a number off this ladder.
    len: lengths.includes(len) ? len : lengths[lengths.length - 1],
    empty: null,
  };
}

/**
 * What a setup screen shows instead of a Start button when the focus has
 * nothing left in it.
 *
 * The reason itself is already on screen as the note under the title — see
 * `focusSetup` — so all that is left here is the way out. Said before Start
 * rather than after it, because "Nothing to study" arriving on a button the
 * screen let you press is the app taking back an offer it should never have
 * made. Reached by a link kept from before a pack was switched, or by setting
 * aside the last bird in a round you had bookmarked.
 */
export function emptyFocus(ctx: ViewContext): HTMLElement {
  return el(
    'button.next.btn.btn-quiet',
    { onclick: () => ctx.back('/library') },
    t('quiz.chrome.exit.library'),
  );
}
